import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { api, apiData } from './api';
import { useAuth } from '@/store/auth';

/**
 * 푸시 등록 — 명세서 §5.1(기기별 푸시 토큰 등록) / §8
 *
 * Capacitor 안드로이드·iOS에서는 FCM 네이티브 토큰을 받고,
 * 브라우저·PWA(아이폰 사파리 홈 화면 추가 포함, iOS 16.4+)에서는
 * VAPID 웹푸시로 등록한다. 권한을 아직 안 준 브라우저에서는 조용히
 * 넘어가고, 알림 화면의 버튼(enableWebPush)으로 사용자가 켠다 —
 * 브라우저·iOS 정책상 권한 요청은 사용자 동작에서만 허용된다.
 */
let registered = false;

/** 이 브라우저에서 웹푸시가 기술적으로 가능한가 */
export function webPushSupported(): boolean {
  return (
    !Capacitor.isNativePlatform() &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

async function subscribeWebPush(): Promise<boolean> {
  const { publicKey } = await apiData<{ publicKey: string | null }>('/notifications/web-push-key');
  if (!publicKey) return false;

  const registration = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));

  await api('/notifications/devices', {
    method: 'POST',
    body: { token: JSON.stringify(subscription.toJSON()), platform: 'WEB' },
  });
  return true;
}

/**
 * 브라우저 알림 켜기 — 반드시 버튼 클릭 등 사용자 동작 안에서 부른다.
 * 성공하면 true. 거부·미지원·키 없음이면 false.
 */
export async function enableWebPush(): Promise<boolean> {
  if (!webPushSupported()) return false;
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return false;
    return await subscribeWebPush();
  } catch {
    return false;
  }
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalized);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

export async function registerPushNotifications(): Promise<void> {
  if (registered) return;

  // 브라우저·PWA — 이미 허용된 경우에만 조용히 (재)구독한다
  if (!Capacitor.isNativePlatform()) {
    if (webPushSupported() && Notification.permission === 'granted') {
      registered = true;
      try {
        await subscribeWebPush();
      } catch {
        registered = false;
      }
    }
    return;
  }

  // FCM 설정(google-services.json) 없이 빌드된 앱에서 register()를 부르면
  // 네이티브(Firebase 미초기화) 크래시가 난다 — JS try/catch로는 못 막으므로
  // CI가 설정을 넣은 빌드(VITE_NATIVE_PUSH_READY=1)에서만 푸시를 켠다.
  if (import.meta.env.VITE_NATIVE_PUSH_READY !== '1') {
    console.warn('[push] FCM 설정이 없는 빌드 — 네이티브 푸시 등록을 건너뜁니다.');
    return;
  }

  registered = true;

  try {
    const permission = await PushNotifications.checkPermissions();
    let granted = permission.receive === 'granted';

    if (!granted) {
      const requested = await PushNotifications.requestPermissions();
      granted = requested.receive === 'granted';
    }
    if (!granted) return;

    await PushNotifications.addListener('registration', (token) => {
      void api('/notifications/devices', {
        method: 'POST',
        body: { token: token.value, platform: Capacitor.getPlatform() === 'ios' ? 'IOS' : 'ANDROID' },
      }).catch(() => {
        // 토큰 등록 실패가 앱 사용을 막지는 않는다. 다음 실행에서 다시 시도된다.
      });
    });

    await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      // 딥링크 — 서버가 target으로 { type, id }를 실어 보낸다 (§8.2).
      const data = action.notification.data as { type?: string; id?: string } | undefined;
      const path = deepLinkFor(data, useAuth.getState().membership?.role);
      if (path) window.location.assign(path);
    });

    await PushNotifications.register();
  } catch {
    registered = false;
  }
}

/**
 * 알림 target → 화면 경로 (§8.2 딥링크).
 *
 * 같은 알림도 역할에 따라 도착지가 다르다 — 발달기록은 학부모에겐 돋보기(/scope),
 * 교직원에겐 기록 탭이다(/scope는 학부모 전용 라우트라 교직원이 가면 404였다).
 * 서버가 보내는 모든 target type을 여기서 받는다 — 빠뜨리면 눌러도 이동하지 않는다.
 */
export function deepLinkFor(
  data?: { type?: string; id?: string },
  role?: string | null,
): string | null {
  if (!data?.type) return null;
  const isParent = role === 'PARENT';

  switch (data.type) {
    case 'dailyNote':
      return data.id ? `/notes/${data.id}` : '/notes';
    case 'notice':
      return data.id ? `/notices/${data.id}` : '/notices';
    case 'developmentRecord':
    case 'developmentTask':
      return isParent ? '/scope' : '/development-tasks';
    case 'lifeRecord':
      return '/life-records';
    case 'attendance':
      return isParent ? '/' : '/attendance';
    case 'homeMessage':
      return isParent ? '/' : '/talk';
    case 'medication':
      return '/medications';
    case 'pickup':
      return '/pickups';
    case 'event':
      return '/schedule';
    case 'meal':
      return '/meals';
    case 'health':
      return '/growth';
    case 'album':
      return data.id ? `/album/${data.id}` : '/album';
    case 'counseling':
      return '/counseling';
    case 'message-thread':
      return data.id ? `/messages/${data.id}` : '/messages';
    case 'subscription':
      // 구독 만료 알림은 원장에게 간다 — 운영 화면에서 처리한다
      return isParent ? '/' : '/operations';
    case 'document-renewal':
      return isParent ? '/renewals' : '/documents';
    case 'document-renewal-board':
      return '/documents';
    case 'consent':
      return '/consents';
    case 'bus-trip':
      // 학부모도 실시간 위치 화면이 생겼다 (§5.20 P6)
      return '/bus';
    case 'pickup-location':
      // 보호자 출발 알림은 담임에게 — 출석부의 "지금 오는 중" 카드에서 본다 (§5.10 v1.22)
      return isParent ? '/pickups' : '/attendance';
    case 'safety-report':
      // 신고는 원장의 앱 관리 화면에서 처리한다 (§5.30)
      return isParent ? '/' : '/operations';
    default:
      return null;
  }
}
